'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import dynamic from 'next/dynamic';

import { MessageCircle } from 'lucide-react';
import { useTranslations } from 'next-intl';

/**
 * Selenos chat widget.
 *
 * A fixed teal FAB (bottom-right, present on every section) that opens a
 * conversation panel. Two answer surfaces, one knowledge base:
 *
 *   1. Live AI agent — the ChatSuite-hosted Selenos agent, reached through the
 *      public widget API (`/api/widget/init` + `/api/widget/message` SSE). The
 *      same agent powers WhatsApp; lead capture happens ChatSuite-side via the
 *      MCP `submit_lead_data` tool (`/api/mcp/lead-capture`). Activated only
 *      when `NEXT_PUBLIC_SELENOS_CHATSUITE_WIDGET_TOKEN` is set.
 *   2. WhatsApp click-to-chat — a `wa.me` deep link to the Selenos business
 *      session, so the prospect can continue the SAME conversation on WhatsApp.
 *      NEVER the personal Privé session (+41763669239).
 *
 * Ecosystem boundary: this component never calls graph.facebook.com. Messaging
 * transport is ChatSuite's; the only WhatsApp touch here is a plain user-facing
 * deep link. Lead lifecycle/consent stays in this repo (MCP route + verticals).
 *
 * When no widget token is configured (e.g. before the ChatSuite agent is
 * provisioned), the panel degrades to the WhatsApp deep link plus the /contact
 * form — no live agent, no errors. Wiring the token in later lights up the live
 * agent with zero code change.
 */

// The conversation panel (SSE streaming, ChatSuite init, fallback surfaces)
// loads on first open so it stays out of the initial page bundle.
const ChatPanel = dynamic(
  () => import('./chat-panel').then((m) => m.ChatPanel),
  { ssr: false },
);

/** Custom event other components dispatch to open the chat (e.g. a hero CTA). */
export const OPEN_CHAT_EVENT = 'selenos:open-chat';

// A3 proactive trigger thresholds: auto-open once after 12s on page OR 60%
// scroll depth, whichever comes first.
const PROACTIVE_DELAY_MS = 12_000;
const PROACTIVE_SCROLL_RATIO = 0.6;
const PROACTIVE_SHOWN_KEY = 'selenos:proactive-shown';

type OpenTrigger = 'manual' | 'auto_time' | 'auto_scroll';

/**
 * Null-safe PostHog capture. posthog-js attaches itself to `window.posthog`
 * after init; if it is absent we no-op rather than throw (mirrors
 * lib/analytics/posthog-events.ts).
 */
function captureEvent(event: string, props?: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;

  const ph = (
    window as unknown as {
      posthog?: { capture?: (e: string, p?: Record<string, unknown>) => void };
    }
  ).posthog;

  ph?.capture?.(event, {
    ...props,
    source: window.location.href,
    timestamp: new Date().toISOString(),
  });
}

export function SelenosChatWidget() {
  const t = useTranslations('selenos');
  const [open, setOpen] = useState(false);
  // Non-null only when the panel was opened by the proactive trigger; carries
  // the proof-anchored opener so the panel greets with it instead of the
  // generic welcome.
  const [proactiveOpener, setProactiveOpener] = useState<string | null>(null);
  const proactiveDone = useRef(false);

  const openChat = useCallback(
    (trigger: OpenTrigger) => {
      proactiveDone.current = true;
      try {
        sessionStorage.setItem(PROACTIVE_SHOWN_KEY, '1');
      } catch {
        // sessionStorage blocked (private mode) — proactiveDone ref still guards.
      }

      if (trigger !== 'manual') {
        setProactiveOpener(t('chatProactiveOpener'));
      }

      setOpen(true);
      captureEvent('selenos_widget_opened', { trigger });
    },
    [t],
  );

  // Let any element on the page open the chat (hero/CTA buttons dispatch
  // OPEN_CHAT_EVENT). The widget owns its open state; this is the only door in.
  useEffect(() => {
    const onOpen = () => openChat('manual');
    window.addEventListener(OPEN_CHAT_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_CHAT_EVENT, onOpen);
  }, [openChat]);

  // A3 proactive trigger: surface the chat once per session for visitors who
  // land, read, and would otherwise bounce without engaging. Skipped if the
  // visitor already opened it (this session) so it never nags.
  useEffect(() => {
    let alreadyShown = false;
    try {
      alreadyShown = sessionStorage.getItem(PROACTIVE_SHOWN_KEY) === '1';
    } catch {
      // ignore
    }
    if (alreadyShown || proactiveDone.current) return;

    const controller = new AbortController();
    let timer = 0;

    const fire = (trigger: 'auto_time' | 'auto_scroll') => {
      if (proactiveDone.current) return;
      window.clearTimeout(timer);
      controller.abort(); // removes the scroll listener below
      openChat(trigger);
    };

    timer = window.setTimeout(() => fire('auto_time'), PROACTIVE_DELAY_MS);

    window.addEventListener(
      'scroll',
      () => {
        const doc = document.documentElement;
        const ratio = (doc.scrollTop + window.innerHeight) / doc.scrollHeight;
        if (ratio >= PROACTIVE_SCROLL_RATIO) fire('auto_scroll');
      },
      { passive: true, signal: controller.signal },
    );

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [openChat]);

  return (
    <div className="fixed right-6 bottom-6 z-50">
      {open ? (
        <ChatPanel
          proactiveOpener={proactiveOpener}
          onClose={() => {
            setOpen(false);
            captureEvent('selenos_widget_closed');
          }}
        />
      ) : (
        <button
          type="button"
          onClick={() => openChat('manual')}
          aria-label={t('chatToggleAria')}
          className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-[var(--sel-accent)] text-[var(--sel-accent-ink)] shadow-lg shadow-black/40 transition-transform duration-200 hover:scale-105 focus-visible:ring-2 focus-visible:ring-[var(--sel-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--sel-bg)] focus-visible:outline-none"
        >
          <MessageCircle className="h-6 w-6" />
        </button>
      )}
    </div>
  );
}
